import NepaliDateLib from 'nepali-date-converter';

// The package exports its class as default; handle both ESM and CJS interop
const NepaliDate = NepaliDateLib.default || NepaliDateLib;

export const BS_MONTHS = [
  'Baisakh', 'Jestha', 'Asar', 'Shrawan', 'Bhadra', 'Ashwin',
  'Kartik',  'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra',
];

// AD month abbreviations, used by adHint (derived straight from the "YYYY-MM" string, no Date).
export const AD_MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Current year in Asia/Kathmandu (UTC+5:45) — never the browser/UTC year.
const KTH_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;
function ktmCurrentYear() {
  return new Date(Date.now() + KTH_OFFSET_MS).getUTCFullYear();
}

// ── Kathmandu day/month KEYS (UTC+5:45, no DST) ─────────────────────────────────────
// The ONLY correct way to bucket/compare a timestamp by calendar day or month in this app.
// `new Date(x).toISOString().slice(0,10)` keys by the UTC day, so a timestamp between
// Kathmandu midnight and 05:45 (i.e. 18:15–24:00 UTC the previous day) lands on the WRONG day —
// these helpers shift into Kathmandu first, then read the civil Y/M/D. A UTC-midnight date
// (e.g. a session stored as new Date('YYYY-MM-DD')) resolves to that same day, unchanged.
const _pad2 = (n) => String(n).padStart(2, '0');
function ktmParts(input) {
  const d = input instanceof Date ? input : new Date(input);
  const k = new Date(d.getTime() + KTH_OFFSET_MS);
  return { y: k.getUTCFullYear(), m: k.getUTCMonth() + 1, d: k.getUTCDate() };
}
// "YYYY-MM-DD" civil day in Kathmandu for any Date/ISO string. Defaults to now.
export function ktmDayKey(input = Date.now()) {
  const { y, m, d } = ktmParts(input);
  return `${y}-${_pad2(m)}-${_pad2(d)}`;
}
// "YYYY-MM" civil month in Kathmandu for any Date/ISO string. Defaults to now.
export function ktmMonthKey(input = Date.now()) {
  const { y, m } = ktmParts(input);
  return `${y}-${_pad2(m)}`;
}
// Today / this month in Kathmandu — the correct replacements for new Date().toISOString().slice(…).
export function ktmToday() { return ktmDayKey(Date.now()); }
export function ktmThisMonth() { return ktmMonthKey(Date.now()); }

// "YYYY-MM-DD" from a Date that ALREADY REPRESENTS a civil day in LOCAL time — e.g. bsToAd()
// output (new Date(y, m-1, d), local midnight). Reads local getters, so it recovers that civil
// day on any browser timezone. Use this (NOT ktmDayKey) for such constructed dates; use ktmDayKey
// for absolute instants (a stored UTC timestamp / paidDate / now).
export function isoLocalDay(date) {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;
}

const BS_MONTH_INITIALS = {
  1: 'B', 2: 'J', 3: 'A', 4: 'S', 5: 'BH', 6: 'AS',
  7: 'K', 8: 'M', 9: 'P', 10: 'MG', 11: 'F', 12: 'C',
};

// AD Date → { year, month (1-indexed), day, monthName }
export function adToBs(date) {
  const d     = date instanceof Date ? date : new Date(date);
  const nd    = new NepaliDate(d);
  const month = nd.getMonth() + 1; // library is 0-indexed
  return {
    year:      nd.getYear(),
    month,
    day:       nd.getDate(),
    monthName: BS_MONTHS[month - 1],
  };
}

// BS (1-indexed month) → JS Date in AD
export function bsToAd(year, month, day) {
  const nd = new NepaliDate(year, month - 1, day);
  return nd.toJsDate();
}

// "2083 Baisakh 12"
export function formatBs(date) {
  const bs = adToBs(date);
  return `${bs.year} ${bs.monthName} ${bs.day}`;
}

// "12 May 2026"
export function formatAd(date) {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// "Mon, 12 May 2026"
export function formatAdNice(date) {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

// "Mon, 12 Baisakh 2083"
export function formatBsNice(date) {
  const bs = adToBs(date);
  const d  = date instanceof Date ? date : new Date(date);
  const wd = d.toLocaleDateString('en-GB', { weekday: 'short' });
  return `${wd}, ${bs.day} ${bs.monthName} ${bs.year}`;
}

// Accurate BS span for the AD range [adStart, adEnd] (inclusive). A period can straddle two BS
// months (and two BS years), so this reads the real BS month at each end — no hardcoded guess.
//   same month        → "Baisakh 2083"
//   two months, 1 yr  → "Shrawan–Bhadra 2083"
//   crosses BS year   → "Chaitra 2082 – Baisakh 2083"
export function bsSpanLabel(adStart, adEnd) {
  const first = adToBs(adStart);
  const last  = adToBs(adEnd);
  if (first.year === last.year) {
    return first.month === last.month
      ? `${first.monthName} ${first.year}`
      : `${first.monthName}–${last.monthName} ${first.year}`;
  }
  return `${first.monthName} ${first.year} – ${last.monthName} ${last.year}`;
}

// BS span that an AD month ("YYYY-MM") overlaps, e.g. "2026-08" → "Shrawan–Bhadra 2083".
// Uses the month's real first and last civil days — accurate, not an approximation.
export function adMonthToBsRangeLabel(yearMonth) {
  const [y, m] = String(yearMonth).split('-').map(Number);
  return bsSpanLabel(new Date(y, m - 1, 1), new Date(y, m, 0)); // day 0 of next month = last day
}

// "04 Jun 2026 / 2083 Jestha 21" — always shows both AD and BS together so no
// surface displays only one calendar. Returns '—' for empty dates.
export function formatAdBs(date) {
  if (!date) return '—';
  try {
    return `${formatAd(date)} / ${formatBs(date)}`;
  } catch {
    return formatAd(date);
  }
}

// "2083 Jestha 21 · 04 Jun 2026" — BS-first string (mirror of formatAdBs, order flipped) for
// string contexts that can't render a muted span. Prefer <DualDate> in JSX for a muted AD.
export function formatBsAd(date) {
  if (!date) return '—';
  try {
    return `${formatBs(date)} · ${formatAd(date)}`;
  } catch {
    return formatBs(date);
  }
}

// App-wide standard for a FULL date: BS primary + AD secondary, returned as PIECES so callers
// style the AD part muted (never a pre-baked string with hardcoded classes). Empty → primary '—'.
//   const { primary, secondary } = formatDateDual(date);
//   <span>{primary} <span className="text-muted">{secondary}</span></span>
export function formatDateDual(date) {
  if (!date) return { primary: '—', secondary: '' };
  try {
    return { primary: formatBs(date), secondary: formatAd(date) };
  } catch {
    return { primary: formatAd(date), secondary: '' }; // last resort — never both blank
  }
}

// Muted AD-month hint for a "YYYY-MM" string, to disambiguate a BS month label — a BS month
// spans two AD months, so "Asar 2083" alone is ambiguous. Derived straight from the string (no
// Date/UTC math). The AD year is shown only when it differs from the current Kathmandu year.
export function adHint(yearMonth) {
  const [y, m] = String(yearMonth).split('-').map(Number);
  const name = AD_MONTH_ABBR[m - 1] || '';
  return y === ktmCurrentYear() ? name : `${name} ${y}`;
}

// "May 2026" or "2083 Baisakh" from a "YYYY-MM" string
export function formatAdMonth(yearMonth) {
  const [y, m] = yearMonth.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export function formatBsMonth(yearMonth) {
  const [y, m] = yearMonth.split('-').map(Number);
  // Sample mid-month (day 15) to match DualDatePicker's adMonthToBs conversion, so a
  // month label always lands in the same BS month the picker shows. Day 1 can fall in
  // the previous BS month (a BS month straddles two AD months).
  const bs = adToBs(new Date(y, m - 1, 15));
  return `${bs.year} ${bs.monthName}`;
}

export function getCurrentBsYear() {
  return adToBs(new Date()).year;
}

export function getBsMonthInitial(month) {
  return BS_MONTH_INITIALS[month] || '';
}

export function getDaysInBsMonth(year, month) {
  if (month < 1 || month > 12) return 30;
  try {
    const nextMonth   = month === 12 ? 1 : month + 1;
    const nextYear    = month === 12 ? year + 1 : year;
    const nextMonthAd = bsToAd(nextYear, nextMonth, 1);
    const lastDayAd   = new Date(nextMonthAd.getTime() - 24 * 60 * 60 * 1000);
    return adToBs(lastDayAd).day;
  } catch {
    return 30;
  }
}
